import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { clipboardService } from '../services/api';
import ClipboardItem from './ClipboardItem';
import { Search, Filter, Star, LogOut, Plus, RefreshCw, Loader2, Globe, FileText, ExternalLink, Image, Settings, TrendingUp, Calendar, FolderOpen, Sparkles, Code, Palette, Music, Camera, BookOpen, Gamepad2, Heart, Users, Clock, MapPin, Tag, Layers, Award, MessageCircle, Share2, Bookmark, Grid, List, ChevronUp, Hash, UserPlus, ArrowLeft, MoreHorizontal, Eye, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import useInfiniteScroll from '../hooks/useInfiniteScroll';

const Dashboard = () => {
  const { user, logout } = useAuth();
  

  const [items, setItems] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    favorite: '',
    content_type: '',
    public: '',
  });
  const [sortOrder, setSortOrder] = useState('date_desc'); // 'date_desc' or 'votes_desc'
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    items_per_page: 20,
  });
  const [hasMore, setHasMore] = useState(true);
  const [stats, setStats] = useState(null);
  // Track favorite changes separately to preserve them across API calls
  const [favoriteChanges, setFavoriteChanges] = useState(new Map());
  // Track user actions to prevent auto-refresh interference
  const [userActionInProgress, setUserActionInProgress] = useState(false);
  // Ref to track the auto-refresh interval
  const autoRefreshIntervalRef = useRef(null);

  // Category editing state
  const [editingItem, setEditingItem] = useState(null);
  const [editCategory, setEditCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  
  // Title editing state
  const [editingTitle, setEditingTitle] = useState(null);
  const [editTitle, setEditTitle] = useState('');

  // Categories dropdown state
  const [showCategoriesDropdown, setShowCategoriesDropdown] = useState(false);

  // Pre-defined icon mapping for reliability
  const categoryIconMap = {
    'tech': Sparkles,
    'tips': Sparkles,
    'help': Sparkles,
    'web': Code,
    'dev': Code,
    'code': Code,
    'development': Code,
    'design': Palette,
    'art': Palette,
    'creative': Palette,
    'productivity': TrendingUp,
    'work': TrendingUp,
    'business': TrendingUp,
    'news': TrendingUp,
    'update': TrendingUp,
    'announcement': TrendingUp,
    'music': Music,
    'audio': Music,
    'sound': Music,
    'photo': Camera,
    'image': Camera,
    'camera': Camera,
    'book': BookOpen,
    'read': BookOpen,
    'article': BookOpen,
    'game': Gamepad2,
    'play': Gamepad2,
    'fun': Gamepad2,
    'health': Heart,
    'fitness': Heart,
    'wellness': Heart,
    'star': Star,
    'favorite': Star,
    'best': Star,
    'world': Globe,
    'global': Globe,
    'international': Globe,
    'settings': Settings,
    'config': Settings,
    'setup': Settings,
    'user': Users,
    'people': Users,
    'community': Users,
    'time': Clock,
    'schedule': Clock,
    'calendar': Clock,
    'location': MapPin,
    'place': MapPin,
    'map': MapPin,
    'tag': Tag,
    'label': Tag,
    'category': Tag,
    'layer': Layers,
    'stack': Layers,
    'group': Layers,
    'trend': TrendingUp,
    'popular': TrendingUp,
    'viral': TrendingUp,
    'award': Award,
    'achievement': Award,
    'recognition': Award,
    'message': MessageCircle,
    'chat': MessageCircle,
    'communication': MessageCircle,
    'plus': Plus,
    'add': Plus,
    'new': Plus,
    'more': MoreHorizontal,
    'extra': MoreHorizontal,
    'additional': MoreHorizontal,
    'share': Share2,
    'social': Share2,
    'network': Share2,
    'bookmark': Bookmark,
    'save': Bookmark,
    'eye': Eye,
    'view': Eye,
    'watch': Eye,
    'grid': Grid,
    'layout': Grid,
    'dashboard': Grid,
    'list': List,
    'menu': List,
    'items': List,
    'chevron': ChevronUp,
    'arrow': ChevronUp,
    'direction': ChevronUp,
    'hash': Hash,
    'number': Hash,
    'pound': Hash,
    'userplus': UserPlus,
    'invite': UserPlus,
    'join': UserPlus
  };

  const getCategoryIcon = (categoryName) => {
    if (!categoryName) return FolderOpen;
    
    const name = categoryName.toLowerCase();
    
    // Check for exact matches first
    for (const [keyword, icon] of Object.entries(categoryIconMap)) {
      if (name.includes(keyword)) {
        return icon;
      }
    }
    
    // Default fallback - always return a valid icon
    return FolderOpen;
  };

  const getCategoryColor = (categoryId) => {
    const colors = [
      'bg-blue-500', 'bg-green-500', 'bg-purple-500', 
      'bg-orange-500', 'bg-red-500', 'bg-pink-500',
      'bg-indigo-500', 'bg-teal-500', 'bg-yellow-500'
    ];
    const index = categoryId.toString().length % colors.length;
    return colors[index];
  };

  const handleEditCategory = (item) => {
    setEditingItem(item.id);
    setEditCategory(item.category_id || '');
  };

  const handleSaveCategory = async (itemId) => {
    try {
      // Call the API to update the category
      const response = await clipboardService.updateItem(itemId, {
        category_id: editCategory
      });
      
      // Find the selected category details
      const selectedCategory = categories.find(cat => cat.id.toString() === editCategory.toString());
      
      // Update local state with the response
      setItems(prevItems => 
        prevItems.map(item => 
          item.id === itemId 
            ? { 
                ...item, 
                category_id: editCategory,
                category_name: selectedCategory?.name || '',
                category_description: selectedCategory?.description || '',
                category_color: selectedCategory?.color || ''
              }
            : item
        )
      );
      
      // Also update the favorites array if the item is in favorites
      setFavorites(prevFavorites =>
        prevFavorites.map(item =>
          item.id === itemId
            ? {
                ...item,
                category_id: editCategory,
                category_name: selectedCategory?.name || '',
                category_description: selectedCategory?.description || '',
                category_color: selectedCategory?.color || ''
              }
            : item
        )
      );
      
      setEditingItem(null);
      setEditCategory('');
      toast.success('Category updated successfully!');
    } catch (error) {
      console.error('Error updating category:', error);
      toast.error('Failed to update category');
    }
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setEditCategory('');
  };

  const handleEditTitle = (item) => {
    setEditingTitle(item.id);
    setEditTitle(item.title || '');
  };

  const handleSaveTitle = async (itemId) => {
    try {
      // Call the API to update the title
      const response = await clipboardService.updateItem(itemId, {
        title: editTitle
      });
      
      // Update local state with the response
      setItems(prevItems => 
        prevItems.map(item => 
          item.id === itemId 
            ? { ...item, title: editTitle }
            : item
        )
      );
      
      // Also update the favorites array if the item is in favorites
      setFavorites(prevFavorites =>
        prevFavorites.map(item =>
          item.id === itemId
            ? { ...item, title: editTitle }
            : item
        )
      );
      
      setEditingTitle(null);
      setEditTitle('');
      toast.success('Title updated successfully!');
    } catch (error) {
      console.error('Error updating title:', error);
      toast.error('Failed to update title');
    }
  };

  const handleCancelTitleEdit = () => {
    setEditingTitle(null);
    setEditTitle('');
  };

  const fetchCategories = useCallback(async () => {
    try {
      setCategoriesLoading(true);
      const response = await clipboardService.getCategories();
      setCategories(response.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to load categories');
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  // Modified fetchItems to preserve local favorite state and support infinite scroll
  const fetchItemsWithLocalState = useCallback(async (page = 1, search = searchTerm, filterParams = filters, append = false) => {
    try {
      const params = {
        page,
        limit: pagination.items_per_page,
        content_type: filterParams.content_type, // Only send content_type filter to API
        public: filterParams.public !== '' ? filterParams.public : undefined,
        order_by: sortOrder === 'votes_desc' ? 'votes:desc' : 'date:desc',
      };
      
      if (search) {
        params.search = search;
      }

      const response = await clipboardService.getItems(params);
      
      // Check if response has items
      if (!response || !response.items) {
        console.error('Dashboard - Invalid response structure:', response);
        throw new Error('Invalid response structure from API');
      }
      
      // Merge with local state to preserve favorite changes
      const mergedItems = (response.items || []).map(apiItem => {
        // Check if we have a local favorite change for this item
        const localFavoriteChange = favoriteChanges.get(apiItem.id);
        if (localFavoriteChange !== undefined) {
          // Apply the local favorite change
          return { ...apiItem, is_favorite: localFavoriteChange };
        }
        return apiItem;
      });
      
      // Apply local filtering
      let filteredItems = mergedItems;
      
      // Apply favorite filter locally
      if (filterParams.favorite === '1') {
        filteredItems = mergedItems.filter(item => item.is_favorite);
      } else if (filterParams.favorite === '0') {
        filteredItems = mergedItems.filter(item => !item.is_favorite);
      }
      
      // Update items - append for infinite scroll, replace for new searches/filters
      setItems(prevItems => {
        if (append) {
          return [...prevItems, ...filteredItems];
        }
        return filteredItems;
      });
      
      // Handle pagination from API response
      if (response.pagination) {
        // Map the actual API response fields to our expected structure
        const apiPagination = response.pagination;
        const mappedPagination = {
          current_page: apiPagination.page || apiPagination.current_page || 1,
          total_pages: apiPagination.pages || apiPagination.total_pages || 1,
          total_items: apiPagination.total || apiPagination.total_items || filteredItems.length,
          items_per_page: pagination.items_per_page || apiPagination.limit || apiPagination.items_per_page || 20,
        };
        setPagination(mappedPagination);
        
        // Update hasMore based on pagination
        setHasMore(mappedPagination.current_page < mappedPagination.total_pages);
      } else {
        // Set default pagination if API doesn't provide it
        setPagination(prev => ({
          current_page: page || 1,
          total_pages: 1,
          total_items: filteredItems.length,
          items_per_page: prev.items_per_page || 20,
        }));
        setHasMore(false);
      }
    } catch (error) {
      toast.error('Failed to load clipboard items');
      console.error('Error fetching items:', error);
    }
  }, [pagination.items_per_page, favoriteChanges, sortOrder]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await clipboardService.getStats();
      setStats(response.stats);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  const fetchFavorites = useCallback(async () => {
    try {
      const response = await clipboardService.getItems({ 
        favorite: '1', 
        limit: 100,
        order_by: sortOrder === 'votes_desc' ? 'votes:desc' : 'date:desc',
      });
      
      if (response && response.items) {
        setFavorites(response.items);
      }
    } catch (error) {
      console.error('Error fetching favorites:', error);
      toast.error('Failed to load favorites');
    }
  }, [sortOrder]);

  const loadData = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchItemsWithLocalState(1), fetchStats(), fetchFavorites(), fetchCategories()]);
    setLoading(false);
  }, [fetchItemsWithLocalState, fetchStats, fetchFavorites, fetchCategories]);

  const refreshData = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([fetchItemsWithLocalState(1), fetchStats(), fetchFavorites()]);
    setRefreshing(false);
    toast.success('Data refreshed!');
  }, [fetchItemsWithLocalState, fetchStats, fetchFavorites]);

  useEffect(() => {
    loadData();
  }, []);

  // Auto-refresh management
  const startAutoRefresh = useCallback(() => {
    if (autoRefreshIntervalRef.current) {
      clearInterval(autoRefreshIntervalRef.current);
    }
    
    autoRefreshIntervalRef.current = setInterval(() => {
      fetchItemsWithLocalState(1);
    }, 30000);
  }, [fetchItemsWithLocalState]);

  const stopAutoRefresh = useCallback(() => {
    if (autoRefreshIntervalRef.current) {
      clearInterval(autoRefreshIntervalRef.current);
      autoRefreshIntervalRef.current = null;
    }
  }, []);

  // Start auto-refresh on mount
  useEffect(() => {
    startAutoRefresh();
    
    // Cleanup on unmount
    return () => {
      stopAutoRefresh();
    };
  }, [startAutoRefresh, stopAutoRefresh]);

  // Stop auto-refresh when user actions are in progress
  useEffect(() => {
    if (userActionInProgress) {
      stopAutoRefresh();
    } else {
      startAutoRefresh();
    }
  }, [userActionInProgress, startAutoRefresh, stopAutoRefresh]);

  // Add effect to detect page refreshes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Page is about to unload/refresh
    };

    const handleUnload = () => {
      // Page is unloading/refreshing
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
    };
  }, []);

  // Infinite scroll callback
  const loadMoreItems = useCallback(() => {
    if (!loading && hasMore) {
      const nextPage = pagination.current_page + 1;
      fetchItemsWithLocalState(nextPage, searchTerm, filters, true);
    }
  }, [loading, hasMore, pagination.current_page, searchTerm, filters, fetchItemsWithLocalState]);

  // Use infinite scroll hook
  const lastElementRef = useInfiniteScroll(loadMoreItems, hasMore, loading);

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, current_page: 1 }));
    setHasMore(true);
    fetchItemsWithLocalState(1, searchTerm, filters, false);
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, current_page: 1 }));
    setHasMore(true);
    fetchItemsWithLocalState(1, searchTerm, newFilters, false);
  };

  const handlePageChange = (page) => {
    fetchItemsWithLocalState(page, searchTerm, filters, false);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    // Just update the pagination state - the useEffect will handle the API call
    setPagination(prev => ({
      ...prev,
      items_per_page: newItemsPerPage,
      current_page: 1 // Reset to first page when changing items per page
    }));
  };

  const handleDelete = (id) => {
    setItems(prev => prev.filter(item => item.id !== id));
    setStats(prev => prev ? { ...prev, total_items: prev.total_items - 1 } : null);
  };

  const handleToggleFavorite = (id, isFavorite) => {
    // Set user action flag to stop auto-refresh
    setUserActionInProgress(true);
    
    // Store the favorite change in our separate state
    setFavoriteChanges(prev => {
      const newChanges = new Map(prev);
      newChanges.set(id, isFavorite);
      return newChanges;
    });
    
    // Update the current items state
    setItems(prev => {
      const updated = prev.map(item => 
        item.id === id ? { ...item, is_favorite: isFavorite } : item
      );
      return updated;
    });
    
    // Update favorites list immediately
    if (isFavorite) {
      // Add to favorites if it's not already there
      setFavorites(prev => {
        const item = items.find(item => item.id === id);
        if (item && !prev.find(fav => fav.id === id)) {
          return [...prev, { ...item, is_favorite: true }];
        }
        return prev;
      });
    } else {
      // Remove from favorites
      setFavorites(prev => prev.filter(item => item.id !== id));
    }
    
    if (stats) {
      setStats(prev => ({
        ...prev,
        favorite_items: isFavorite 
          ? prev.favorite_items + 1 
          : prev.favorite_items - 1
      }));
    }
  };

  const handleFavoriteSuccess = (id, isFavorite) => {
    // Clear the change from our cache since it's now saved to the API
    setFavoriteChanges(prev => {
      const newChanges = new Map(prev);
      newChanges.delete(id);
      return newChanges;
    });
    
    // Refresh favorites list to ensure it's in sync with the API
    fetchFavorites();
    
    // Clear the user action flag to restart auto-refresh
    setUserActionInProgress(false);
  };

  const handleTogglePublic = (id, isPublic) => {
    // Set user action flag to stop auto-refresh
    setUserActionInProgress(true);
    
    // Update the current items state
    setItems(prev => {
      const updated = prev.map(item => 
        item.id === id ? { ...item, is_public: isPublic } : item
      );
      return updated;
    });
    
    // Also update the favorites state if the item is in favorites
    setFavorites(prev => {
      const updated = prev.map(item => 
        item.id === id ? { ...item, is_public: isPublic } : item
      );
      return updated;
    });
    
    // Clear the user action flag immediately since public toggle doesn't have a success callback
    setTimeout(() => {
      setUserActionInProgress(false);
    }, 1000);
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
  };

  const clearFilters = () => {
    const newFilters = { favorite: '', content_type: '', public: '' };
    setFilters(newFilters);
    setSearchTerm('');
    fetchItemsWithLocalState(1, '', newFilters);
  };

  const handleSortToggle = () => {
    const newSortOrder = sortOrder === 'date_desc' ? 'votes_desc' : 'date_desc';
    setSortOrder(newSortOrder);
    
    // Create new params with the updated sort order
    const newParams = {
      page: 1,
      limit: pagination.items_per_page,
      content_type: filters.content_type,
      public: filters.public !== '' ? filters.public : undefined,
      order_by: newSortOrder === 'votes_desc' ? 'votes:desc' : 'date:desc',
    };
    
    if (searchTerm) {
      newParams.search = searchTerm;
    }

    // Refresh data with new sort order
    const refreshData = async () => {
      try {
        // Fetch main items with new sort order
        const itemsResponse = await clipboardService.getItems(newParams);
        
        // Fetch favorites with new sort order
        const favoritesResponse = await clipboardService.getItems({ 
          favorite: '1', 
          limit: 100,
          order_by: newSortOrder === 'votes_desc' ? 'votes:desc' : 'date:desc',
        });
        
        // Update items state
        if (itemsResponse && itemsResponse.items) {
          const mergedItems = itemsResponse.items.map(apiItem => {
            const localFavoriteChange = favoriteChanges.get(apiItem.id);
            if (localFavoriteChange !== undefined) {
              return { ...apiItem, is_favorite: localFavoriteChange };
            }
            return apiItem;
          });
          
          let filteredItems = mergedItems;
          if (filters.favorite === '1') {
            filteredItems = mergedItems.filter(item => item.is_favorite);
          } else if (filters.favorite === '0') {
            filteredItems = mergedItems.filter(item => !item.is_favorite);
          }
          
          setItems(filteredItems);
        }
        
        // Update favorites state
        if (favoritesResponse && favoritesResponse.items) {
          setFavorites(favoritesResponse.items);
        }
        
        // Update pagination
        if (itemsResponse && itemsResponse.pagination) {
          const apiPagination = itemsResponse.pagination;
          const mappedPagination = {
            current_page: apiPagination.page || apiPagination.current_page || 1,
            total_pages: apiPagination.pages || apiPagination.total_pages || 1,
            total_items: apiPagination.total || apiPagination.total_items || 0,
            items_per_page: pagination.items_per_page || apiPagination.limit || apiPagination.items_per_page || 20,
          };
          setPagination(mappedPagination);
          setHasMore(mappedPagination.current_page < mappedPagination.total_pages);
        }
        
      } catch (error) {
        console.error('Error refreshing data with new sort order:', error);
        toast.error('Failed to refresh data');
      }
    };
    
    refreshData();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin h-8 w-8 text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading your clipboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <Link to="/" className="flex flex-col items-start hover:opacity-80 transition-opacity">
                <img src="/big-clipboard-text-logo.jpg" alt="Big Clipboard Logo" className="h-10 w-auto" />
                <span className="text-sm text-primary-700 font-medium mt-1">Copy Once. Use Anywhere.</span>
              </Link>
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/public">
                <button className="btn-secondary flex items-center">
                  <Globe className="h-4 w-4 mr-2" />
                  Public Clipboard
                </button>
              </Link>

              {(user?.is_admin === true || user?.is_admin === "true" || user?.is_admin === 1) && (
                <Link to="/admin">
                  <button className="bg-red-100 hover:bg-red-200 text-red-700 border-red-300 px-4 py-2 rounded-lg flex items-center transition-colors">
                    <Settings className="h-4 w-4 mr-2" />
                    Admin
                  </button>
                </Link>
              )}
              <button
                onClick={handleLogout}
                className="btn-secondary flex items-center"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Welcome message above stats */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-center">
        <span className="block text-xs sm:text-sm text-gray-500 mt-2 text-center">Welcome back, {user?.full_name}</span>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        {stats && (
          <div className="flex flex-col md:flex-row gap-4 mb-8 w-full">
            {/* Each stat box should flex-grow and fill available space on desktop */}
            <div className="card flex-1 flex flex-row items-center gap-3">
                <FileText className="h-6 w-6 text-primary-600" />
                <div className="flex flex-row items-baseline gap-2">
                  <div className="text-2xl font-bold text-primary-600">{stats.total_items}</div>
                  <div className="text-sm text-gray-600 whitespace-nowrap">Total Items</div>
                </div>
              </div>
            <div className="card flex-1 flex flex-row items-center gap-3">
                <Star className="h-6 w-6 text-yellow-600" />
                <div className="flex flex-row items-baseline gap-2">
                  <div className="text-2xl font-bold text-yellow-600">{stats.favorite_items}</div>
                  <div className="text-sm text-gray-600 whitespace-nowrap">Favorites</div>
                </div>
              </div>
            <div className="card flex-1 flex flex-row items-center gap-3">
                <FileText className="h-6 w-6 text-blue-600" />
                <div className="flex flex-row items-baseline gap-2">
                  <div className="text-2xl font-bold text-blue-600">{stats.text_items}</div>
                  <div className="text-sm text-gray-600 whitespace-nowrap">Text Items</div>
                </div>
              </div>
            <div className="card flex-1 flex flex-row items-center gap-3">
                <Image className="h-6 w-6 text-green-600" />
                <div className="flex flex-row items-baseline gap-2">
                  <div className="text-2xl font-bold text-green-600">{stats.image_items || 0}</div>
                  <div className="text-sm text-gray-600 whitespace-nowrap">Images</div>
                </div>
              </div>
          </div>
        )}

        {/* Clipboard Items - Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Items Column */}
          <div className="lg:col-span-2">
            {/* Search and Filters - Directly above All Items */}
            <div className="card mb-6">
              <form onSubmit={handleSearch} className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="w-full sm:w-1/2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search clipboard items..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="input-field pl-10"
                      />
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button type="submit" className="btn-primary">
                      Search
                    </button>
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="btn-secondary text-sm"
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center space-x-2">
                    <Filter className="h-4 w-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-700">Filters:</span>
                  </div>
                  
                  <select
                    value={filters.favorite}
                    onChange={(e) => handleFilterChange('favorite', e.target.value)}
                    className="input-field w-32"
                  >
                    <option value="">All items</option>
                    <option value="1">Favorites only</option>
                    <option value="0">Non-favorites</option>
                  </select>

                  <select
                    value={filters.content_type}
                    onChange={(e) => handleFilterChange('content_type', e.target.value)}
                    className="input-field w-32"
                  >
                    <option value="">All types</option>
                    <option value="text">Text</option>
                    <option value="link">Links</option>
                    <option value="image">Images</option>
                    <option value="long_text">Long Text</option>
                    <option value="html">HTML</option>
                  </select>

                  <select
                    value={filters.public}
                    onChange={(e) => handleFilterChange('public', e.target.value)}
                    className="input-field w-32"
                  >
                    <option value="">All visibility</option>
                    <option value="1">Public only</option>
                    <option value="0">Private only</option>
                  </select>
                </div>
              </form>
            </div>

            <div className="mb-4 flex items-center justify-between">
                      <div className="flex items-center space-x-4">
          <h2 className="text-lg font-semibold text-gray-900">All Items</h2>
          {/* <button
            type="button"
            onClick={() => setShowCategoriesDropdown(!showCategoriesDropdown)}
            className="btn-secondary text-sm flex items-center"
          >
            <FolderOpen className="w-4 h-4 mr-1" />
            Categories ({categories.length})
          </button> */}
        </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">
                  {sortOrder === 'date_desc' ? 'Sorted by Date' : 'Sorted by Votes'}
                </span>
                <button
                  onClick={handleSortToggle}
                  className="flex items-center space-x-2 px-3 py-1.5 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded-lg transition-colors text-sm font-medium"
                  title={sortOrder === 'date_desc' 
                    ? 'Click to sort by votes (highest first). Items made public can be seen and voted on by other users.' 
                    : 'Click to sort by date (newest first)'}
                >
                  {sortOrder === 'date_desc' ? (
                    <>
                      <TrendingUp className="w-4 h-4" />
                      <span>Votes</span>
                    </>
                  ) : (
                    <>
                      <Calendar className="w-4 h-4" />
                      <span>Date</span>
                    </>
                  )}
                </button>
              </div>
            </div>
            <div className="space-y-4">
              {items.filter(item => !item.is_favorite).length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-gray-400 mb-4">
                    <Search className="h-8 w-8 mx-auto" />
                  </div>
                  <h3 className="text-md font-medium text-gray-900 mb-2">No regular items</h3>
                  <p className="text-sm text-gray-600">
                    {searchTerm || Object.values(filters).some(f => f !== '')
                      ? 'Try adjusting your search or filters'
                      : 'All your items are favorited!'}
                  </p>
                </div>
              ) : (
                <>
                  {items.filter(item => !item.is_favorite).map((item, index) => (
                    <div
                      key={item.id}
                      ref={index === items.filter(item => !item.is_favorite).length - 1 ? lastElementRef : null}
                    >
                      <ClipboardItem
                        item={item}
                        onDelete={handleDelete}
                        onToggleFavorite={handleToggleFavorite}
                        onFavoriteSuccess={handleFavoriteSuccess}
                        onTogglePublic={handleTogglePublic}
                        onEditCategory={handleEditCategory}
                        onSaveCategory={handleSaveCategory}
                        onCancelEdit={handleCancelEdit}
                        onEditTitle={handleEditTitle}
                        onSaveTitle={handleSaveTitle}
                        onCancelTitleEdit={handleCancelTitleEdit}
                        editingItem={editingItem}
                        editCategory={editCategory}
                        setEditCategory={setEditCategory}
                        editingTitle={editingTitle}
                        editTitle={editTitle}
                        setEditTitle={setEditTitle}
                        categories={categories}
                        categoriesLoading={categoriesLoading}
                        isOwner={true}
                      />
                    </div>
                  ))}
                  {/* Infinite scroll loading indicator */}
                  {hasMore && (
                    <div className="text-center py-4">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto text-indigo-600" />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Favorites Column */}
          <div className="lg:col-span-1">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                <Star className="h-5 w-5 text-yellow-500 mr-2" />
                Favorites
              </h2>
            </div>
            <div className="space-y-4">
              {favorites.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-gray-400 mb-4">
                    <Star className="h-8 w-8 mx-auto" />
                  </div>
                  <h3 className="text-md font-medium text-gray-900 mb-2">No favorites yet</h3>
                  <p className="text-sm text-gray-600">
                    Star important items to see them here
                  </p>
                </div>
              ) : (
                favorites.map((item) => (
                  <ClipboardItem
                    key={item.id}
                    item={item}
                    onDelete={handleDelete}
                    onToggleFavorite={handleToggleFavorite}
                    onFavoriteSuccess={handleFavoriteSuccess}
                    onTogglePublic={handleTogglePublic}
                    onEditCategory={handleEditCategory}
                    onSaveCategory={handleSaveCategory}
                    onCancelEdit={handleCancelEdit}
                    onEditTitle={handleEditTitle}
                    onSaveTitle={handleSaveTitle}
                    onCancelTitleEdit={handleCancelTitleEdit}
                    editingItem={editingItem}
                    editCategory={editCategory}
                    setEditCategory={setEditCategory}
                    editingTitle={editingTitle}
                    editTitle={editTitle}
                    setEditTitle={setEditTitle}
                    categories={categories}
                    categoriesLoading={categoriesLoading}
                    isOwner={true}
                    isCompact={true}
                  />
                ))
              )}
            </div>
          </div>
        </div>

        {/* Items count info */}
        {items.length > 0 && (
          <div className="flex justify-center mt-8">
            <div className="text-sm text-gray-600">
              Showing {items.length} of {pagination.total_items || items.length} items
            </div>
          </div>
        )}

        {/* Categories Dropdown Overlay */}
        {showCategoriesDropdown && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black bg-opacity-25 z-40"
              onClick={() => setShowCategoriesDropdown(false)}
            />
            
            {/* Dropdown */}
            <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-w-md w-full mx-4 max-h-96 overflow-y-auto">
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Categories</h3>
                  <button
                    onClick={() => setShowCategoriesDropdown(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              <div className="p-4">
                {categoriesLoading ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-indigo-600" />
                    <p className="text-sm text-gray-600 mt-2">Loading categories...</p>
                  </div>
                ) : categories.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-gray-400 mb-2">
                      <FolderOpen className="h-8 w-8 mx-auto" />
                    </div>
                    <h3 className="text-sm font-medium text-gray-900 mb-1">No categories</h3>
                    <p className="text-xs text-gray-600">
                      Categories will appear here
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {categories.map((category) => {
                      const IconComponent = getCategoryIcon(category.name);
                      const categoryColor = category.color || getCategoryColor(category.id);
                      
                      return (
                        <div
                          key={category.id}
                          className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                        >
                          <div className={`w-8 h-8 ${categoryColor} rounded-lg flex items-center justify-center flex-shrink-0`}>
                            <IconComponent className="w-4 h-4 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-gray-900 text-sm truncate">
                              {category.name}
                            </h3>
                            {category.description && (
                              <p className="text-xs text-gray-500 truncate mt-1">
                                {category.description}
                              </p>
                            )}
                            <div className="flex items-center space-x-2 mt-1">
                              <span className="text-xs text-gray-400 flex items-center">
                                <FileText className="w-3 h-3 mr-1" />
                                {category.item_count || 0} items
                              </span>
                              <span className="text-xs text-gray-400 flex items-center">
                                <Users className="w-3 h-3 mr-1" />
                                {category.member_count || 0} members
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      
    </div>
  );
};

export default Dashboard; 